import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Mail, Clock, ExternalLink, Share2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  trackPurchase,
  buildContentIds,
  buildContents,
  generateEventId,
  sendServerEvent,
} from "@/lib/facebook-pixel";
import { trackPurchase as trackGA4Purchase, buildGA4Item } from "@/lib/analytics";
import { NewsletterSignup } from "@/components/newsletter";

interface SessionData {
  id: string;
  status: string;
  customerEmail: string;
  amountTotal: number;
  metadata: {
    // Customer info
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    customerState?: string;
    // Product info
    biome?: string;
    tier?: string;
    terrariumStyle?: string;
    deliveryWeek?: string;
    // Gift info
    isGift?: string;
    giftRecipientName?: string;
    giftOccasion?: string;
    giftMessage?: string;
    // Additional options
    specialRequests?: string;
    insuranceOptIn?: string;
    wantsInsurance?: string; // Legacy field
  };
}

function SocialShareButtons({ orderDetails }: { orderDetails: SessionData }) {
  const shareText = `Just ordered a beautiful handcrafted terrarium from Life Itself Studio! Can't wait to bring some nature into my space.`;
  const shareUrl = "https://lifeitself.studio";

  const handleTwitterShare = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleFacebookShare = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert("Link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted-foreground">Share your excitement:</p>
      <div className="flex gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={handleTwitterShare}
          className="flex items-center gap-2"
          data-testid="button-share-twitter"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          Twitter
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleFacebookShare}
          className="flex items-center gap-2"
          data-testid="button-share-facebook"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          Facebook
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          className="flex items-center gap-2"
          data-testid="button-share-copy"
        >
          <Share2 className="w-4 h-4" />
          Copy Link
        </Button>
      </div>
    </div>
  );
}

export default function CheckoutSuccess() {
  const [, setLocation] = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const purchaseTracked = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("session_id");
    if (id) {
      setSessionId(id);
    }
  }, []);

  const { data: sessionData, isLoading, error } = useQuery<SessionData>({
    queryKey: ["/api/checkout/session", sessionId],
    queryFn: async () => {
      const response = await fetch(`/api/checkout/session/${sessionId}`);
      if (!response.ok) {
        throw new Error("Failed to fetch session");
      }
      return response.json();
    },
    enabled: !!sessionId,
  });

  // Track purchase events for analytics (Facebook Pixel + GA4)
  useEffect(() => {
    if (!sessionData || purchaseTracked.current) return;
    if (sessionData.status !== 'paid' && sessionData.status !== 'complete') return;

    purchaseTracked.current = true;

    const metadata = sessionData.metadata || {};
    const biome = metadata.biome || metadata.terrariumStyle || 'forest';
    const hasPremiumPlants = metadata.tier === 'premium';
    const hasCustomDesign = metadata.tier === 'custom';
    const experienceType = (metadata.experienceSize === '2ppl' ? '2-person' :
                          metadata.experienceSize === '4ppl' ? '4-person' : 'none') as 'none' | '2-person' | '4-person';
    const wantsInsurance = metadata.insuranceOptIn === 'true' || metadata.wantsInsurance === 'true';
    const isGift = metadata.isGift === 'true';

    // Build checkout state for FB tracking
    const checkoutState = {
      biome,
      hasPremiumPlants,
      hasCustomDesign,
      experienceType,
      wantsInsurance,
      isGift,
    };

    // Calculate value in dollars
    const purchaseValue = (sessionData.amountTotal || 0) / 100;

    // Generate event ID for deduplication with server-side tracking
    const eventId = generateEventId();

    // Build content arrays
    const contentIds = buildContentIds(checkoutState);
    const contents = buildContents(checkoutState);

    // Track Facebook Pixel Purchase event
    trackPurchase({
      contentIds,
      value: purchaseValue,
      numItems: contents.length,
      contents,
      orderId: sessionData.id,
      eventId,
    });

    // Also send to server for Conversions API deduplication
    sendServerEvent({
      eventName: 'Purchase',
      eventId,
      eventTime: Math.floor(Date.now() / 1000),
      eventSourceUrl: window.location.href,
      userData: {
        email: sessionData.customerEmail,
      },
      customData: {
        contentIds,
        value: purchaseValue,
        currency: 'USD',
        numItems: contents.length,
        orderId: sessionData.id,
      },
    });

    // Track GA4 purchase event
    const ga4Items = buildGA4Item(
      biome,
      hasPremiumPlants,
      hasCustomDesign,
      experienceType
    );

    trackGA4Purchase(
      sessionData.id,
      purchaseValue,
      ga4Items,
      {
        shipping: 0, // Free local delivery
        tax: 0,
      }
    );

    console.log('[Analytics] Purchase tracked for order:', sessionData.id);
  }, [sessionData]);

  const formatStyleName = (styleId: string, biome?: string, tier?: string) => {
    // New format - use tier and biome
    if (tier) {
      const tierNames: Record<string, string> = {
        base: "Base Terrarium",
        premium: "Premium Biome",
        custom: "Custom Design",
      };
      const biomeName = biome === "desert" ? "Desert" : "Forest";
      return `${tierNames[tier] || "Terrarium"} - ${biomeName}`;
    }

    // Style-based naming (new checkout)
    const styleNames: Record<string, string> = {
      forest: "Forest Oasis",
      desert: "DesertScape",
      tropical: "Tropical Paradise",
      deluxe: "Deluxe Custom",
    };
    if (styleNames[styleId]) {
      return styleNames[styleId];
    }

    // Legacy format
    const legacyStyles: Record<string, string> = {
      "forest-oasis": "Forest Oasis",
      "desert-scape": "DesertScape",
      "tropical-paradise": "Tropical Paradise",
      "deluxe-custom": "Deluxe Custom",
    };
    return legacyStyles[styleId] || styleId || "Terrarium";
  };

  const formatDeliveryWeek = (weekStart: string) => {
    if (!weekStart) return "To be confirmed";
    const date = new Date(weekStart);
    const weekEnd = new Date(date);
    weekEnd.setDate(date.getDate() + 6);
    const formatDate = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `Week of ${formatDate(date)} - ${formatDate(weekEnd)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your order details...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">
              We couldn't load your order details, but don't worry - your order has been received!
            </p>
            <Button onClick={() => setLocation("/")} data-testid="button-back-home">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="overflow-hidden">
            <CardHeader className="bg-primary/5 border-b border-border text-center pb-8 pt-10">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="mx-auto mb-4"
              >
                <CheckCircle className="w-16 h-16 text-primary" />
              </motion.div>
              <CardTitle className="font-serif text-3xl md:text-4xl" data-testid="text-success-heading">
                Thank you for your order!
              </CardTitle>
              <p className="text-muted-foreground mt-2" data-testid="text-customer-name">
                {sessionData.metadata?.customerName || "Valued Customer"}
              </p>
            </CardHeader>

            <CardContent className="p-6 md:p-8 space-y-8">
              {/* Order Summary */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg" data-testid="text-order-summary-title">
                  Order Summary
                </h3>
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Terrarium Style</span>
                    <span className="font-medium" data-testid="text-terrarium-style">
                      {formatStyleName(
                        sessionData.metadata?.terrariumStyle || "",
                        sessionData.metadata?.biome,
                        sessionData.metadata?.tier
                      )}
                    </span>
                  </div>
                  {sessionData.metadata?.biome && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Biome</span>
                      <span className="font-medium capitalize" data-testid="text-biome">
                        {sessionData.metadata.biome}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery Week</span>
                    <span className="font-medium" data-testid="text-delivery-week">
                      {formatDeliveryWeek(sessionData.metadata?.deliveryWeek || "")}
                    </span>
                  </div>
                  {(sessionData.metadata?.insuranceOptIn === "true" || sessionData.metadata?.wantsInsurance === "true") && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Plant Insurance</span>
                      <span className="font-medium text-primary" data-testid="text-insurance">
                        Included
                      </span>
                    </div>
                  )}
                  {sessionData.metadata?.isGift === "true" && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Gift For</span>
                      <span className="font-medium" data-testid="text-gift-recipient">
                        {sessionData.metadata.giftRecipientName || "Someone Special"}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-border pt-3 flex justify-between">
                    <span className="font-semibold">Total Paid</span>
                    <span className="font-semibold text-lg" data-testid="text-total-amount">
                      ${((sessionData.amountTotal || 0) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Review Notice */}
              <div className="flex items-start gap-4 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <Clock className="w-6 h-6 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 dark:text-amber-400" data-testid="text-review-title">
                    Your order is being reviewed
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-500 mt-1" data-testid="text-review-desc">
                    We'll review your order within 24 hours and confirm all the details. Ryan will personally
                    reach out if we have any questions about your custom preferences.
                  </p>
                </div>
              </div>

              {/* Confirmation Email Notice */}
              <div className="flex items-start gap-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Mail className="w-6 h-6 text-blue-600 dark:text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800 dark:text-blue-400" data-testid="text-email-title">
                    Confirmation email sent
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-500 mt-1" data-testid="text-email-desc">
                    A confirmation email has been sent to{" "}
                    <span className="font-medium">{sessionData.customerEmail}</span>.
                    Please check your inbox (and spam folder) for order details.
                  </p>
                </div>
              </div>

              {/* Customer Portal Link */}
              <div className="text-center p-4 border border-border rounded-lg">
                <h4 className="font-medium mb-2">Track Your Order</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Access your customer portal to track order status, view care instructions,
                  and manage your terrarium subscription.
                </p>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 mx-auto"
                  onClick={() => {
                    // Portal link would be sent via email with unique token
                    alert("Check your email for your personal portal link!");
                  }}
                  data-testid="button-portal-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  Customer Portal
                </Button>
              </div>

              {/* Newsletter Signup */}
              <div className="pt-4 border-t border-border">
                <NewsletterSignup
                  source="checkout"
                  variant="card"
                  title="Stay Connected"
                  description="Get terrarium care tips, exclusive offers, and early access to new designs."
                  showName={false}
                  showConsents={false}
                  biomePreference={sessionData.metadata?.biome}
                />
              </div>

              {/* Social Share */}
              <div className="pt-4 border-t border-border">
                <SocialShareButtons orderDetails={sessionData} />
              </div>

              {/* Back Home */}
              <div className="text-center pt-4">
                <Button
                  variant="ghost"
                  onClick={() => setLocation("/")}
                  data-testid="button-continue-browsing"
                >
                  Continue Browsing
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
